import template from "./image-upload.html";
import "./image-upload.less";
import {
	EVENT_CLEAR_VALUE,
	EVENT_REVERT_VALUES,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import { find } from "lodash";
import {
	buildValidationOptions,
	isAllowedSize,
	isAcceptedMimeType,
	createBlobUrl,
	triggerFileDownload
} from "utils/files.js";
import { alertFileSizeExceeded } from "../prompts/alertFileSizeExceeded.js";
import { alertInvalidFileType } from "../prompts/alertInvalidFileType.js";

export class Controller {
	constructor(
		$scope,
		eventBus,
		fileHandleStore,
		appState,
		$logger,
		fileUploadService,
		loadingService
	) {
		this._scope = $scope;
		this.fileHandleStore = fileHandleStore;
		this.appState = appState;
		this.$logger = $logger;
		this.loadingService = loadingService;
		this.fileUploadService = fileUploadService;
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		this._unregister = this.reportFormCtrl.registerFieldController(this);
		this.fieldKey = this.fileHandleStore.buildKey(this.field.Id, this.repeatId);
		this.inputId = `image-upload-input-${this.field.Id}-${this.repeatId ?? "base"}`;
		this.dropActive = false;
		this.showOverlay = false;
		this.validationOptions = buildValidationOptions(this.field.AllowedMimeTypes, this.field.MaxSize);
		this.maxSize = this.validationOptions.maxSize;
		this.acceptTypes = this.validationOptions.mimeTypes.length > 0
			? this.validationOptions.mimeTypes.join(",")
			: "image/*";
		this.initialValue = find(this.values, v => v.FieldId === this.field.Id);
		this.initialHandle = this.fileUploadService.normalizeServerFile(this.initialValue?.File);
		this.initialHandleId = this.initialHandle?.id;

		this.setValue();
		this.loadStoredFile();
		this.resolveImageSrc();

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), () => {
			this.clearFile();
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
		if(this._imageBlobUrl) {
			URL.revokeObjectURL(this._imageBlobUrl);
		}
	}

	resolveImageSrc() {
		if(!this.handle) {
			this.imageSrc = undefined;
			return;
		}

		if(this.handle.$uri) {
			this.imageSrc = this.handle.$uri;
			return;
		}

		if(this.handle.id) {
			this.fetchServerImageSrc(this.handle.id);
		}
	}

	async fetchServerImageSrc(fileId) {
		try {
			const formId = this.appState?.forms?.selected?.Id;
			const reportId = this.reportFormCtrl?.reportId;

			if(!formId || !reportId) {
				return;
			}

			const handle = { id: fileId, mimeType: this.handle?.mimeType };
			const data = await this.fileUploadService.fetchFileData(formId, reportId, handle);

			if(data) {
				if(this._imageBlobUrl) {
					URL.revokeObjectURL(this._imageBlobUrl);
				}
				this._imageBlobUrl = createBlobUrl(data, this.handle?.mimeType);
				this.imageSrc = this._imageBlobUrl;
				this._scope.$applyAsync();
			}
		}catch(err) {
			this.$logger.error("Failed to resolve image source", err);
		}
	}

	openBrowse() {
		if(this.field.isReadonly || this.field.isDisabled) {
			return;
		}

		const input = document.getElementById(this.inputId);
		if(input) {
			input.click();
		}
	}

	async handleFileSelected(file) {
		if(!file) {
			return;
		}

		if(!await this.validateFile(file)) {
			return;
		}

		this.fileHandleStore.clearKey(this.fieldKey);

		const stored = this.fileHandleStore.storeForKey(this.fieldKey, file);
		this.handle = stored?.handle;
		this.handleId = stored?.handle?.id;
		this.fileInfo = stored?.info;
		this.resolveImageSrc();
		this.checkForDirty();
		this._scope.$applyAsync();
	}

	clearFile() {
		this.fileHandleStore.clearKey(this.fieldKey);

		this.handleId = undefined;
		this.handle = undefined;
		this.fileInfo = undefined;
		this.imageSrc = undefined;
		this.checkForDirty();
	}

	async downloadFile($event) {
		if($event) {
			$event.stopPropagation();
		}

		if(!this.handle) {
			return;
		}

		const formId = this.appState?.forms?.selected?.Id;
		const reportId = this.reportFormCtrl?.reportId;

		if(!this.handle.$uri && (!formId || !reportId)) {
			return;
		}

		try {
			const promise = this.fileUploadService.fetchFileData(formId, reportId, this.handle);
			this.loadingService.watch(promise, "Downloading...");
			const data = await promise;
			if(data) {
				triggerFileDownload(data, this.handle.name, this.handle.mimeType);
			}
		}catch(err) {
			this.$logger.error("Failed to download file", err);
		}
	}

	async validateFile(file) {
		if(!isAllowedSize(file.size, this.maxSize)) {
			await alertFileSizeExceeded(this.field.Name, file.size, this.maxSize);
			return false;
		}

		if(!isAcceptedMimeType(file.type, this.validationOptions.mimeTypes)) {
			await alertInvalidFileType(this.field.Name, file.type, this.validationOptions.mimeTypes);
			return false;
		}

		return true;
	}

	setValue(val) {
		if(val) {
			this.handle = val;
			this.handleId = val?.id;
			this.fileInfo = this.fileHandleStore._buildInfo(val);
			return;
		}

		const value = find(this.values, v => v.FieldId === this.field.Id);
		const handle = this.fileUploadService.normalizeServerFile(value?.File);

		if(handle) {
			this.handle = handle;
			this.handleId = handle.id;
			this.fileInfo = this.fileHandleStore._buildInfo(handle);
		}
	}

	loadStoredFile() {
		const stored = this.fileHandleStore.getByKey(this.fieldKey);
		this.fileInfo = stored?.info || this.fileHandleStore._buildInfo(this.handle);

		if(stored?.handle?.$uri) {
			this.handle = stored.handle;
			this.handleId = stored.handle.id;
		}
	}


	openOverlay() {
		if(this.imageSrc) {
			this.showOverlay = true;
		}
	}

	closeOverlay() {
		this.showOverlay = false;
	}

	buildResult() {
		if(!this.handle) {
			return undefined;
		}

		const fileHandle = {
			Id: this.handle?.id,
			Name: this.handle?.name,
			Length: this.handle?.length,
			MimeType: this.handle?.mimeType,
			$uri: this.handle?.$uri
		};
		const fieldId = this.field.Id;

		const stored = this.fileHandleStore.getByKey(this.fieldKey);
		const isExistingServerFile = this.handle?.id && !stored?.file;

		if(isExistingServerFile) {
			return {
				FieldId: fieldId,
				File: fileHandle
			};
		}

		return {
			FieldId: fieldId,
			File: fileHandle,
			$metadata: {
				$materialize: async() => {
					const stored = this.fileHandleStore.getByKey(this.fieldKey);
					const file = stored?.file;

					if(!file) {
						throw new Error("invalid file field state for file upload");
					}

					const formId = this.appState?.forms?.selected?.Id;
					if(!formId) {
						throw new Error("invalid file field state for file upload");
					}

					const localUri = this.handle?.$uri;
					const materializedHandle = await this.fileUploadService.processFileUpload(file, formId, this.field.Id);
					materializedHandle.$uri = localUri;

					this.handle = materializedHandle;
					this.handleId = materializedHandle.id;
					this.fileInfo = this.fileHandleStore._buildInfo(materializedHandle);
					this.resolveImageSrc();

					return [this.buildFinalValueFromHandle(materializedHandle)];
				}
			}
		};
	}

	buildFinalValueFromHandle(handle) {
		if(!handle) {
			return undefined;
		}

		return {
			FieldId: this.field.Id,
			File: {
				Id: handle.id,
				Name: handle.name,
				Length: handle.length,
				MimeType: handle.mimeType
			}
		};
	}

	getCurrentValue(isApiCall = false) {
		const result = this.buildResult();

		if(result && !isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true) {
			result.IsDirty = !this.fileUploadService.areHandlesEqual(this.handle, this.initialHandle);
		}

		return result ? [result] : [];
	}

	checkForDirty() {
		const isDirty = (this.field.visible !== false || this.initialHandle !== undefined)
			&& !this.fileUploadService.areHandlesEqual(this.handle, this.initialHandle);
		this.setFieldDirty(this.field.Id, isDirty);
	}

	revertValue() {
		if(this.initialHandleId) {
			this.handle = this.initialHandle;
			this.handleId = this.initialHandleId;
			this.fileInfo = this.fileHandleStore._buildInfo(this.initialHandle);
			this.loadStoredFile();
		} else {
			this.clearFile();
		}
		this.resolveImageSrc();
	}

	setPristine() {
		this.initialHandle = this.handle;
		this.initialHandleId = this.handleId;
	}
}

export default angular
	.module("vesl.components.report-form.image-upload", [])
	.component("imageUpload", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			repeatId: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
